import { CpuChipIcon } from "@heroicons/react/24/outline";
import {
  RadioCardGroup,
  RadioCardIndicator,
  RadioCardItem,
} from "components/Graphs/RadioCardGroup";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { GPUInstanceType } from "types/InstanceTypes";
import React, { useMemo } from "react";
import {
  getInstanceTypeArchitectures,
  normalizeArchitectures,
} from "utils/architecture";
import FilterCard, { FilterRemoveButton } from "./FilterCard";

interface ArchitectureFilterProps {
  allInstances: GPUInstanceType[];
  selectedArchitecture: string;
  setSelectedArchitecture: (architecture: string) => void;
}

const ArchitectureFilter: React.FC<ArchitectureFilterProps> = ({
  allInstances,
  selectedArchitecture,
  setSelectedArchitecture,
}) => {
  const architectureOptions = useMemo(
    () =>
      normalizeArchitectures(
        allInstances.flatMap(getInstanceTypeArchitectures),
      ).sort((a, b) => a.localeCompare(b)),
    [allInstances],
  );

  if (architectureOptions.length === 0) return null;

  return (
    <Popover>
      <div className="relative inline-flex">
        <PopoverTrigger asChild>
          <FilterCard
            icon={<CpuChipIcon className="h-4 w-4" />}
            title={
              selectedArchitecture === "any"
                ? "All architectures"
                : selectedArchitecture
            }
            filterApplied={selectedArchitecture !== "any"}
            hasTrailingAction={selectedArchitecture !== "any"}
            count={
              selectedArchitecture === "any"
                ? architectureOptions.length
                : undefined
            }
          />
        </PopoverTrigger>
        {selectedArchitecture !== "any" && (
          <FilterRemoveButton
            filterName="architecture"
            onRemove={() => setSelectedArchitecture("any")}
          />
        )}
      </div>
      <PopoverContent className="w-[calc(100vw-2rem)] max-w-[500px] overflow-y-auto p-4">
        <div className="mb-4 flex w-full flex-col">
          <RadioCardGroup
            className="flex flex-col space-y-2"
            value={selectedArchitecture}
            onValueChange={setSelectedArchitecture}
          >
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                  CPU Architectures
                </span>
                <div className="h-px flex-1 bg-zinc-800" />
                {selectedArchitecture !== "any" && (
                  <button
                    className="text-sm text-highlight"
                    onClick={() => setSelectedArchitecture("any")}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {architectureOptions.map((architecture) => (
                  <RadioCardItem
                    key={architecture}
                    value={architecture}
                    className="p-3"
                  >
                    <div className="flex items-center gap-3">
                      <RadioCardIndicator className="mt-1" />
                      <span className="text-xs uppercase text-white">
                        {architecture}
                      </span>
                    </div>
                  </RadioCardItem>
                ))}
              </div>
            </div>
          </RadioCardGroup>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ArchitectureFilter;
